import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChartNoAxesCombined } from 'lucide-react';
import { Cartao, Indicador, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { data, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { hojeNoFuso } from '../../componentes/periodo/periodo';
import { CHAVE, SeletorQuadro, useQuadroEscolhido, useQuadros, useRotuloColuna, useTempoRealTarefas } from './comum';
import type { Estatistica, Painel as DadosPainel } from './tipos';

const PERIODOS = ['30', '90', '180', '365'] as const;

/** Data AAAA-MM-DD menos N dias (conta de calendário, sem fuso: a data já vem no fuso do workspace). */
function menosDias(iso: string, dias: number) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
}

function useDias() {
  const { t } = useTranslation('tarefas');
  return (v: number | null) => (v === null ? '-' : t('painel.dias', { count: v, valor: numero(v, v % 1 ? 1 : 0) }));
}

function detalheEstat(e: Estatistica, t: (k: string, o?: Record<string, unknown>) => string, dias: (v: number | null) => string) {
  return e.n ? t('painel.mediaMediana', { mediana: dias(e.mediana), p85: dias(e.p85), n: numero(e.n) }) : t('painel.semConcluidos');
}

/** Barra de proporção (dados em ciano), com o número escrito ao lado: a barra nunca é a única informação. */
function Barra({ pct }: { pct: number }) {
  return (
    <div className="mt-1.5 h-1.5 rounded-full bg-sup-3" role="presentation">
      <div className="h-1.5 rounded-full bg-ciano" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

function Conteudo({ p, quadroId }: { p: DadosPainel; quadroId: string }) {
  const { t } = useTranslation('tarefas');
  const dias = useDias();
  const rotuloColuna = useRotuloColuna();
  const semDado = p.lead_time.n === 0 && p.idade.abertos === 0 && p.por_coluna.every((c) => c.passagens === 0) && p.origem.totais.length === 0;
  if (semDado) {
    return (
      <Cartao semPadding>
        <Vazio icone={<ChartNoAxesCombined className="size-5" aria-hidden />} titulo={t('painel.vazioTitulo')} texto={t('painel.vazioTexto')} />
      </Cartao>
    );
  }
  const maxColuna = Math.max(0, ...p.por_coluna.map((c) => c.media_dias ?? 0));
  const maxVazao = Math.max(0, ...p.vazao.map((v) => v.concluidos));
  const totalIdade = p.idade.abertos || 1;
  return (
    <div className="flex flex-col gap-4">
      {p.lead_time.n < 5 && (
        <p role="status" className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">
          {t('painel.poucoDado')}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador rotulo={t('painel.leadTime')} valor={dias(p.lead_time.media)} detalhe={`${t('painel.leadAjuda')}. ${detalheEstat(p.lead_time, t, dias)}`} cor="ciano" />
        <Indicador rotulo={t('painel.cycleTime')} valor={dias(p.cycle_time.media)} detalhe={`${t('painel.cycleAjuda')}. ${detalheEstat(p.cycle_time, t, dias)}`} />
        <Indicador
          rotulo={t('painel.esperaPessoa')}
          valor={dias(p.espera_pessoa.media_dias)}
          detalhe={p.espera_pessoa.n ? t('painel.esperaAjuda', { pct: `${numero(p.espera_pessoa.percentual_do_lead ?? 0, 1)}%` }) : t('painel.esperaSem')}
          cor="laranja"
        />
        <Indicador rotulo={t('painel.wip')} valor={numero(p.wip.total)} detalhe={t('painel.wipAjuda')} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo={t('painel.porColuna')}>
          <p className="mb-3 text-xs text-tinta-3">{t('painel.porColunaAjuda')}</p>
          <ul className="flex flex-col gap-3">
            {p.por_coluna.map((c) => (
              <li key={c.coluna_id} className="min-w-0">
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <TextoCortado className="font-medium">{rotuloColuna(c)}</TextoCortado>
                    {c.gargalo && <Selo cor="laranja">{t('painel.gargalo')}</Selo>}
                  </span>
                  <span className="numero shrink-0 text-tinta-2">{dias(c.media_dias)}</span>
                </div>
                <Barra pct={maxColuna ? ((c.media_dias ?? 0) / maxColuna) * 100 : 0} />
                <p className="mt-1 text-xs text-tinta-3">
                  {t('painel.passagens', { count: c.passagens })} · {t('painel.atuais', { count: c.atuais })}
                </p>
              </li>
            ))}
          </ul>
        </Cartao>

        <Cartao titulo={t('painel.vazao')}>
          <p className="mb-3 text-xs text-tinta-3">{t('painel.vazaoAjuda')}</p>
          <ul className="flex flex-col gap-2">
            {p.vazao.map((v) => (
              <li key={v.semana} className="min-w-0">
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-tinta-2">{t('painel.semana', { data: data(v.semana) })}</span>
                  <span className="numero shrink-0">{t('painel.concluidos', { count: v.concluidos })}</span>
                </div>
                <Barra pct={maxVazao ? (v.concluidos / maxVazao) * 100 : 0} />
              </li>
            ))}
          </ul>
        </Cartao>

        <Cartao titulo={t('painel.idade')}>
          <p className="mb-3 text-xs text-tinta-3">{t('painel.idadeAjuda')}</p>
          {p.idade.abertos === 0 ? (
            <p className="text-sm text-tinta-3">{t('painel.semAbertos')}</p>
          ) : (
            <>
              <ul className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {(['ate_2', 'de_3_a_7', 'de_8_a_14', 'mais_14'] as const).map((f) => (
                  <li key={f} className="min-w-0 rounded-lg border border-borda p-3">
                    <p className="text-xs text-tinta-3">{t(`painel.faixas.${f}`)}</p>
                    <p className={`numero mt-1 text-lg font-semibold ${f === 'mais_14' && p.idade.faixas[f] ? 'text-perigo' : ''}`}>{numero(p.idade.faixas[f])}</p>
                    <Barra pct={(p.idade.faixas[f] / totalIdade) * 100} />
                  </li>
                ))}
              </ul>
              <p className="kicker mb-2">{t('painel.maisParados')}</p>
              <ul className="-mx-2 flex flex-col">
                {p.idade.mais_parados.map((c) => (
                  <li key={c.id}>
                    <Link to={`/operacao/tarefas/kanban?quadro=${quadroId}&card=${c.id}`} className="flex min-w-0 items-center gap-3 rounded-md px-2 py-1.5 hover:bg-sup-2">
                      <span className="numero shrink-0 text-xs text-tinta-3">{`#${c.numero}`}</span>
                      <span className="min-w-0 flex-1">
                        <TextoCortado className="block text-sm font-medium text-tinta-1">{textoLivre(c.titulo)}</TextoCortado>
                        <TextoCortado className="block text-xs text-tinta-3">{`${rotuloColuna({ nome: c.coluna, codigo: c.coluna_codigo })} · ${t('painel.paradoHa', { count: c.dias_parado })}`}</TextoCortado>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Cartao>

        <div className="flex flex-col gap-4">
          <Cartao titulo={t('painel.retrabalho')}>
            {p.retrabalho.cards_com_volta ? (
              <>
                <p className="numero text-2xl font-semibold">{`${numero(p.retrabalho.taxa ?? 0, 1)}%`}</p>
                <p className="mt-1 text-sm text-tinta-2">{t('painel.retrabalhoTexto', { count: p.retrabalho.voltas, cards: numero(p.retrabalho.cards_com_volta), movidos: numero(p.retrabalho.cards_movidos), voltas: numero(p.retrabalho.voltas) })}</p>
              </>
            ) : (
              <p className="text-sm text-tinta-3">{t('painel.retrabalhoSem')}</p>
            )}
          </Cartao>

          <Cartao titulo={t('painel.origem')}>
            <p className="mb-3 text-xs text-tinta-3">{t('painel.origemAjuda')}</p>
            {p.origem.totais.length === 0 ? (
              <p className="text-sm text-tinta-3">{t('painel.semOrigem')}</p>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap gap-2">
                  {p.origem.totais.map((o) => (
                    <Selo key={o.origem} cor={o.origem === 'agente' ? 'roxo' : o.origem === 'chave' ? 'azul' : 'neutro'}>{`${t(`origens.${o.origem}`)}: ${numero(o.n)}`}</Selo>
                  ))}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-tinta-3">
                      <th className="py-1 pr-3 font-medium">{t('campos.origem')}</th>
                      <th className="py-1 font-medium">{t('campos.solicitante')}</th>
                      <th className="py-1 text-right font-medium whitespace-nowrap">{t('painel.quantidade')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borda">
                    {p.origem.por_quem.map((o, i) => (
                      <tr key={i}>
                        <td className="py-1.5 pr-3 align-top">{t(`origens.${o.origem}`)}</td>
                        <td className="py-1.5 pr-3 align-top break-words">{o.quem ? textoLivre(o.quem) : t('painel.quemSem')}</td>
                        <td className="numero py-1.5 text-right whitespace-nowrap">{numero(o.n)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Cartao>
        </div>
      </div>

      <Cartao titulo={t('painel.previsibilidade')}>
        <p className="mb-3 text-xs text-tinta-3">{t('painel.previsibilidadeAjuda')}</p>
        {p.previsibilidade.length === 0 ? (
          <p className="text-sm text-tinta-3">{t('painel.semPrevisao')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-tinta-3">
                <th className="py-1 font-medium">{t('painel.tamanho')}</th>
                <th className="py-1 text-right font-medium whitespace-nowrap">{t('painel.cards')}</th>
                <th className="py-1 text-right font-medium whitespace-nowrap">{t('painel.mediana')}</th>
                <th className="py-1 text-right font-medium whitespace-nowrap">{t('painel.p85')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borda">
              {p.previsibilidade.map((x) => (
                <tr key={x.tamanho ?? 'sem'}>
                  <td className="py-1.5">{x.tamanho ? t(`tamanhos.${x.tamanho}`) : t('painel.semTamanho')}</td>
                  <td className="numero py-1.5 text-right whitespace-nowrap">{numero(x.n)}</td>
                  <td className="numero py-1.5 text-right whitespace-nowrap">{dias(x.mediana)}</td>
                  <td className="numero py-1.5 text-right font-semibold whitespace-nowrap">{dias(x.p85)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Cartao>
    </div>
  );
}

/** Aba Painel: a medição do fluxo do quadro (ficha Tarefas), calculada no servidor a partir do histórico de movimento. */
export function Painel() {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  useTempoRealTarefas();
  const quadros = useQuadros();
  const [quadroId, escolherQuadro] = useQuadroEscolhido(quadros.data?.itens);
  const [periodo, setPeriodo] = useState<(typeof PERIODOS)[number]>('90');
  const ate = hojeNoFuso(s.workspace.fuso);
  const de = menosDias(ate, Number(periodo) - 1);
  const q = useQuery({
    queryKey: [...CHAVE, 'painel', quadroId, de, ate],
    queryFn: () => get<DadosPainel>(`/tarefas/quadros/${quadroId}/painel`, { de, ate }),
    enabled: !!quadroId,
  });
  return (
    <Estado consulta={quadros}>
      {(lq) =>
        !lq.itens.length ? (
          <Cartao semPadding>
            <Vazio texto={t('kanban.semQuadro')} />
          </Cartao>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-2">
              <SeletorQuadro quadros={lq.itens} valor={quadroId} aoMudar={escolherQuadro} />
              <label className="flex w-full flex-col gap-1 text-xs text-tinta-3 sm:w-56">
                {t('campos.periodo')}
                <Selecao aria-label={t('campos.periodo')} opcoes={PERIODOS.map((x) => ({ valor: x, rotulo: t(`painel.periodos.${x}`) }))} value={periodo} onChange={(e) => setPeriodo(e.target.value as (typeof PERIODOS)[number])} />
              </label>
            </div>
            {quadroId && <Estado consulta={q}>{(p) => <Conteudo p={p} quadroId={quadroId} />}</Estado>}
          </>
        )
      }
    </Estado>
  );
}
